<script setup>
/*
    Display an infographic value.

    Components:
        icon: <v-icon>
        header: <label>
        primary: <span>
        prefix: <span>
        value: <span>
        sparkline: <span>
        suffix: <span>

    Component Args
        None

    CSS Properties:
        background
        color
        ...

    Examples:
        background: rgb(var(--v-theme-primary))
        color: rgb(var(--v-theme-on-primary))
        font-size: 200px
        font-weight: bold
        suffix: 'msec'
        value: 100
 */
import {reactive, ref} from 'vue'
import {getWidgetProps} from '@/paks/vu-dash/BoardUtils'
import {formatValue} from '@/paks/js-format'
import {State} from '@/paks/vu-state'

defineExpose({update})

const props = defineProps({
    widget: Object,
})

const gradients = [
    ['#222'],
    ['#42b3f4'],
    ['red', 'orange', 'yellow'],
    ['purple', 'violet'],
    ['#00c6ff', '#F0F', '#FF0'],
    ['#f72047', '#ffd200', '#1feaea'],
]

const page = reactive({
    args: {},
    data: {},

    // Samples
    width: 2,
    radius: 10,
    padding: 8,
    lineCap: 'round',
    gradient: gradients[5],
    gradientDirection: 'top',
    gradients,
    fill: false,
    type: 'trend',
    autoLineWidth: false,
})

const header = ref(null)
const icon = ref(null)
const primary = ref(null)
const prefix = ref(null)
const sparkline = ref(null)
const suffix = ref(null)
const value = ref(null)

const DefaultStyles = {
    header: {
        'font-size': 'max(14px, min(min(7cqw, 7cqh), 20px))',
        'text-transform': 'uppercase',
        color: 'rgb(var(--v-theme-text))',
        'font-weight': 'normal',
        opacity: State.app.dark ? '0.75' : '1',
    },
    icon: {
        'font-size': 'max(16px, min(min(12cqw, 30cqh), 72px))',
    },
    prefix: {
        'font-size': 'max(10px, min(min(5cqw, 5cqh), 36px))',
    },
    primary: {
        'font-size': 'max(16px, min(min(10cqw, 10cqh), 80px))',
        color: 'rgb(var(--v-theme-text-lighten-1))',
    },
    sparkline: {
        height: '60cqh',
        width: '100cqw',
    },
    suffix: {
        'font-size': 'max(8px, min(min(7cqw, 7cqh), 16px))',
    },
    value: {
        'font-size': 'max(32px, min(min(10cqw, 10cqh), 80px))',
        'font-weight': 'bold',
    },
}

function update() {
    let widget = props.widget
    let points = widget.value?.points?.map((p) => p.value) || []
    Object.assign(
        page,
        getWidgetProps(
            widget,
            {header, icon, primary, prefix, sparkline, suffix, value},
            {data: points, multipoint: true, styles: DefaultStyles}
        )
    )
    let {data} = page
    if (!Array.isArray(data.primary)) {
        // If not an array, make it one (database values are not arrays)
        data.primary = [data.primary - 0]
    }
    let numeric = 0
    if (widget.statistic == 'current') {
        // Find the last point with a valid sample
        let point = widget.value.points.reverse().find(p => p.count > 0)
        if (point) {
            numeric = point.value
        } else {
            numeric = 0
        }
    } else {
        let count = 0
        for (let value of Object.values(data.primary)) {
            numeric += value - 0
            if (value) count++
        }
        if (widget.statistic == 'average' && count > 0) {
            numeric = numeric / count
        }
    }
    page.numeric = formatValue(numeric, {format: widget.format})
}
</script>

<template>
    <vu-div ref="primary" class="info-widget">
        <div class="content">
            <div v-if="props.widget?.icon">
                <v-icon ref="icon" class="icon">{{ props.widget.icon }}</v-icon>
            </div>
            <div class="details">
                <label ref="header" class="header">{{ widget.header }}</label>
                <span ref="prefix" class="prefix">{{ page.data.prefix }}</span>
                <span ref="value" class="value" v-bind="page.args">{{ page.numeric }}</span>
                <span ref="suffix" class="suffix">{{ page.data.suffix }}</span>
            </div>
        </div>
        <v-sparkline
            v-if="Array.isArray(page.data.primary) && page.data.primary.length > 1"
            ref="sparkline"
            class="sparkline"
            :auto-line-width="page.autoLineWidth"
            :fill="page.fill"
            :gradient="page.gradient"
            :gradient-direction="page.gradientDirection"
            :line-width="page.width"
            :model-value="page.data.primary"
            :padding="0"
            :smooth="page.radius || false"
            :stroke-linecap="page.lineCap"
            :type="page.type"
            auto-draw></v-sparkline>
    </vu-div>
</template>

<style lang="scss">
.info-widget {
    container-type: size;
    position: relative;
    display: inline-grid;
    height: 100cqh !important;
    width: 100cqw !important;
    .content {
        display: flex;
        gap: 2cqw;
        margin: 6cqh 4cqw 0cqh 4cqw;
        .header {
            display: block;
        }
        .icon {
            margin-right: 5cqw;
        }
        .details {
            margin-left: 2cqw;
        }
    }
    .sparkline {
        padding-bottom: 20px;
        width: 100% !important;
        height: 60cqh;
    }
}
</style>
